/**
 * The anchor's one storage location, shared by the two writers: the
 * foreground check-in (`session-store.ts` — every publish path funnels
 * through `checkInAt`) and the background stay-visible task. Split from
 * `presence-anchor.ts` so the pure decisions stay loadable by node tests;
 * this file touches `storage` and is asserted by source anchors instead.
 *
 * Everything here is best-effort and silent: the anchor is an optimisation
 * cache, and a failed read or write must degrade to "no anchor" — which the
 * classifier treats as "publish", today's behaviour — never break a check-in
 * or a background wake over bookkeeping.
 */
import { storage } from "@/lib/storage";
import { withTimeout } from "@/lib/with-timeout";
import {
  anchorFromPublishedFix,
  parseAnchor,
  serializeAnchor,
  type PresenceAnchor,
} from "@/lib/presence-anchor";

const ANCHOR_KEY = "opncue.presence.anchor";

/**
 * Every operation here is bounded, not merely caught. A try/catch handles a
 * REJECTED keychain promise; it does nothing for one that never settles, and
 * this repo has already met that hang twice (`with-timeout.ts`: "the bridge
 * promises to call back and nothing guarantees it ever does";
 * `background-attempt-memory.ts` bounds the identical SecureStore shape).
 * The stakes here are concrete: `checkInAt` awaits the write before the
 * go-visible flow may continue, and the background task awaits the read
 * before it can classify — a wedged keychain must cost this budget at most,
 * after which the cache degrades to "no anchor" (publish), never a stall.
 * 2000 ms for the same reason `PERSISTENCE_BUDGET_MS` chose it.
 */
const ANCHOR_STORAGE_BUDGET_MS = 2000;

export async function readPresenceAnchor(
  expectedOwnerId: string,
): Promise<PresenceAnchor | null> {
  // `withTimeout` never rejects and collapses "failed" and "hung" to null,
  // which parseAnchor already treats as "no anchor" — the publish direction.
  // The ownership check is parseAnchor's (pure, tested there): an anchor a
  // late-settling write left behind for a DIFFERENT account is refused at
  // read, which is the only point this code controls once a native write is
  // in flight.
  return parseAnchor(
    await withTimeout(storage.get(ANCHOR_KEY), ANCHOR_STORAGE_BUDGET_MS),
    expectedOwnerId,
  );
}

export async function writePresenceAnchor(
  anchor: PresenceAnchor,
): Promise<void> {
  await withTimeout(
    storage.set(ANCHOR_KEY, serializeAnchor(anchor)),
    ANCHOR_STORAGE_BUDGET_MS,
  );
}

/**
 * Record a publication the server just accepted. A fix without accuracy
 * CLEARS the cache rather than skipping the write: the server now holds an
 * anchor it cannot apply radius rules to, and a stale-but-precise device copy
 * would let the task stay quiet against a position the server is not using.
 */
export async function recordPublishedAnchor(fix: {
  ownerId: string;
  lat: number;
  lng: number;
  accuracyM: number | null;
}): Promise<void> {
  const anchor = anchorFromPublishedFix(fix, fix.ownerId, new Date().toISOString());
  if (anchor) {
    await withTimeout(
      storage.set(ANCHOR_KEY, serializeAnchor(anchor)),
      ANCHOR_STORAGE_BUDGET_MS,
    );
  } else {
    await withTimeout(storage.remove(ANCHOR_KEY), ANCHOR_STORAGE_BUDGET_MS);
  }
}

export async function clearPresenceAnchor(): Promise<void> {
  await withTimeout(storage.remove(ANCHOR_KEY), ANCHOR_STORAGE_BUDGET_MS);
}
